import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
  Chip,
  createFilterOptions,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import Swal from "sweetalert2";
import { v4 } from "uuid";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { Product, noImage } from "../product.entity";
import { productRepository } from "../product.repository";
import { productCategoriesRepository } from "../product-categories.repository";
import { productSupplierRepository } from "../product-supplier.repository";
import type { ProductCategory } from "../product-category.entity";
import type { ProductSupplier } from "../product-supplier.entity";
import { ProductCategory as ProductCategoryEntity } from "../product-category.entity";
import { ProductSupplier as ProductSupplierEntity } from "../product-supplier.entity";
import { aiBulkProductsExtract } from "../../ai/callable-ai";
import { AI_CREDIT_COSTS, AiCreditCostHint } from "../../ai/ai-credit-guard";
import { isInsufficientAiCreditsError } from "../../ai/ai-credit-guard";
import { readFileAsBase64 } from "../../ai/file-utils";
import { findBestCategoryId, findBestSupplierId } from "../utils/bulk-import-match";
import { parseProductsFromCsv } from "../utils/parse-csv-bulk";
import { matchCatalogForBulkRow, scoredCandidatesForMatch } from "../utils/bulk-catalog-match";
import type { ProductSupplierCostEntry } from "../product.entity";
import { CashRegisterReportHero } from "../../cash-register/components/cash-register-report-hero";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";
import { recordPriceHistory } from "../product-price-history.service";

type DraftRow = {
  id: string;
  /** Novo cadastro completo ou apenas somar quantidade ao estoque de um produto já existente. */
  rowMode: "create" | "stock";
  existingProductId: string | null;
  /** SKU / código de barras (novo cadastro). */
  code: string;
  name: string;
  /** Preço bruto detectado/informado. Interpretação depende de priceType. */
  price: number;
  /** Preço de venda calculado pela margem quando priceType="cost". */
  salePrice: number | null;
  /** Se "cost": price = custo de compra; se "sell": price = preço de venda. */
  priceType: "sell" | "cost";
  /** Modo stock: novo preço de venda a definir (null/0 = manter atual). */
  stockSalePrice: number | null;
  /** Modo stock: preço de custo para o fornecedor selecionado. */
  purchasePrice: number | null;
  stock: number;
  categoryId: string | null;
  supplierId: string | null;
  suggestedCategoryId: string | null;
  suggestedCategoryName: string | null;
  suggestedSupplierId: string | null;
  suggestedSupplierName: string | null;
  trackStock: boolean;
};

type AiResultModalData = { newRows: DraftRow[] };

function parseBRL(s: string): number | null {
  const clean = s.replace(/R\$\s?/, "").trim();
  if (!clean) return null;
  const lastComma = clean.lastIndexOf(",");
  const lastPeriod = clean.lastIndexOf(".");
  const normalized =
    lastComma > lastPeriod
      ? clean.replace(/\./g, "").replace(",", ".")
      : clean.replace(/,/g, "");
  const v = parseFloat(normalized);
  return isNaN(v) || v < 0 ? null : v;
}

function CurrencyField({
  value,
  onChange,
  label,
  size = "small",
  fullWidth,
  helperText,
  placeholder = "R$ 0,00",
  disabled,
  error,
  sx,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  label?: string;
  size?: "small" | "medium";
  fullWidth?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  sx?: object;
}) {
  const [focused, setFocused] = useState(false);
  const [inputStr, setInputStr] = useState("");
  const formatted =
    value != null && value > 0
      ? new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value)
      : "";
  return (
    <TextField
      label={label}
      size={size}
      fullWidth={fullWidth}
      helperText={helperText}
      placeholder={placeholder}
      disabled={disabled}
      error={error}
      sx={sx}
      value={focused ? inputStr : formatted}
      onFocus={() => {
        setInputStr(value != null && value > 0 ? value.toFixed(2).replace(".", ",") : "");
        setFocused(true);
      }}
      onBlur={() => {
        setFocused(false);
        onChange(parseBRL(inputStr));
      }}
      onChange={(e) => setInputStr(e.target.value)}
    />
  );
}

type MatchSelection = {
  rowId: string;
  mode: "new" | "existing";
  existingProductId: string | null;
};

function draftRowToStockMode(r: DraftRow, p: Product): DraftRow {
  const cat = p.getCategory();
  const sup = p.getSulier();
  return {
    ...r,
    rowMode: "stock",
    existingProductId: p.id,
    name: p.getName(),
    price: p.getPrice(),
    salePrice: null,
    priceType: "sell",
    stockSalePrice: p.getPrice() > 0 ? p.getPrice() : null,
    purchasePrice: null,
    categoryId: cat?.id ?? null,
    supplierId: sup?.id ?? null,
    suggestedCategoryId: null,
    suggestedCategoryName: null,
    suggestedSupplierId: null,
    suggestedSupplierName: null,
    trackStock: p.getTrackStock(),
    code: p.getCode() ?? "",
  };
}

function emptyRow(): DraftRow {
  return {
    id: v4(),
    rowMode: "create",
    existingProductId: null,
    code: "",
    name: "",
    price: 0,
    salePrice: null,
    priceType: "sell",
    stockSalePrice: null,
    purchasePrice: null,
    stock: 1,
    categoryId: null,
    supplierId: null,
    suggestedCategoryId: null,
    suggestedCategoryName: null,
    suggestedSupplierId: null,
    suggestedSupplierName: null,
    trackStock: true,
  };
}

function normalizeLabel(s: string) {
  return s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

function mapAiToRows(
  products: Array<{
    name: string;
    code?: string | null;
    price?: number | null;
    stock?: number | null;
    categoryName?: string | null;
    supplierName?: string | null;
    categoryExplicit?: boolean | null;
    supplierExplicit?: boolean | null;
  }>,
  categories: ProductCategory[],
  suppliers: ProductSupplier[]
): DraftRow[] {
  return products.map((p) => {
    const categoryName = p.categoryName?.trim() || null;
    const supplierName = p.supplierName?.trim() || null;
    const matchedCategoryId = findBestCategoryId(categoryName, categories);
    const matchedSupplierId = findBestSupplierId(supplierName, suppliers);
    const categoryExplicit = p.categoryExplicit === true && Boolean(categoryName);
    const supplierExplicit = p.supplierExplicit === true && Boolean(supplierName);
    const categoryShouldAutoAssign = categoryExplicit && Boolean(matchedCategoryId);
    const supplierShouldAutoAssign = supplierExplicit && Boolean(matchedSupplierId);
    return {
      id: v4(),
      rowMode: "create" as const,
      existingProductId: null,
      code: (p.code ?? "").trim(),
      name: p.name?.trim() || "Sem nome",
      price: typeof p.price === "number" && !Number.isNaN(p.price) ? Math.max(0, p.price) : 0,
      salePrice: null,
      priceType: "sell" as const,
      stockSalePrice: null,
      purchasePrice: null,
      stock:
        typeof p.stock === "number" && !Number.isNaN(p.stock) && p.stock >= 0 ?
          Math.floor(p.stock) :
          1,
      categoryId: categoryShouldAutoAssign ? matchedCategoryId : null,
      supplierId: supplierShouldAutoAssign ? matchedSupplierId : null,
      suggestedCategoryId: !categoryShouldAutoAssign ? matchedCategoryId : null,
      suggestedCategoryName: !categoryShouldAutoAssign ? categoryName : null,
      suggestedSupplierId: !supplierShouldAutoAssign ? matchedSupplierId : null,
      suggestedSupplierName: !supplierShouldAutoAssign ? supplierName : null,
      trackStock: true,
    };
  });
}

type AiResultModalDialogProps = {
  open: boolean;
  newRows: DraftRow[];
  catalog: Product[];
  suppliers: ProductSupplier[];
  onConfirm: (patchedRows: DraftRow[]) => void;
  onClose: () => void;
  onSupplierCreated: (s: ProductSupplier) => void;
};

function AiResultModalDialog({
  open,
  newRows,
  catalog,
  suppliers: suppliersProp,
  onConfirm,
  onClose,
  onSupplierCreated,
}: AiResultModalDialogProps) {
  const theme = useTheme();
  const [step, setStep] = useState<"match" | "config">("match");
  const [matchSelections, setMatchSelections] = useState<MatchSelection[]>([]);
  const [localSuppliers, setLocalSuppliers] = useState<ProductSupplier[]>(suppliersProp);
  const [modalSupplier, setModalSupplier] = useState<ProductSupplier | null>(null);
  const [priceType, setPriceType] = useState<"sell" | "cost">("sell");
  const [margin, setMargin] = useState<number>(0);
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);

  useEffect(() => { setLocalSuppliers(suppliersProp); }, [suppliersProp]);

  const rowCandidates = useMemo(
    () =>
      newRows.map((row) => ({
        rowId: row.id,
        row,
        ...scoredCandidatesForMatch({ code: row.code, name: row.name }, catalog),
      })),
    [newRows, catalog]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    setModalSupplier(null);
    setPriceType("sell");
    setMargin(0);
    setSkipConfirmOpen(false);
    const selections: MatchSelection[] = newRows.map((row) => {
      const { autoByCode } = scoredCandidatesForMatch({ code: row.code, name: row.name }, catalog);
      return { rowId: row.id, mode: autoByCode ? ("existing" as const) : ("new" as const), existingProductId: autoByCode?.id ?? null };
    });
    setMatchSelections(selections);
    const anyMatch = rowCandidates.some((rc) => rc.autoByCode != null || rc.candidates.length > 0);
    setStep(anyMatch ? "match" : "config");
  }, [open]);

  const hasAnyPrice = newRows.some((r) => r.price > 0);
  const priceCount = newRows.filter((r) => r.price > 0).length;
  const firstPricedRow = newRows.find((r) => r.price > 0);
  const exampleSale =
    margin > 0 && firstPricedRow
      ? Math.round(firstPricedRow.price * (1 + margin / 100) * 100) / 100
      : 0;

  const proceedToConfig = (selections: MatchSelection[]) => {
    setMatchSelections(selections);
    setStep("config");
  };

  const handleSkipConfirmed = () => {
    setSkipConfirmOpen(false);
    proceedToConfig(
      newRows.map((row) => {
        const { autoByCode } = scoredCandidatesForMatch({ code: row.code, name: row.name }, catalog);
        return { rowId: row.id, mode: autoByCode ? "existing" : "new", existingProductId: autoByCode?.id ?? null };
      })
    );
  };

  const createSupplierByTitle = async (title: string) => {
    if (!title) return;
    const created = new ProductSupplierEntity(v4(), title);
    await productSupplierRepository.save(created);
    setLocalSuppliers((prev) => [...prev, created]);
    setModalSupplier(created);
    onSupplierCreated(created);
  };

  const handleConfirm = () => {
    const patchedRows: DraftRow[] = newRows.map((row) => {
      const sel = matchSelections.find((s) => s.rowId === row.id);
      let updated: DraftRow = { ...row, priceType };
      if (modalSupplier) updated = { ...updated, supplierId: modalSupplier.id };
      // Compute salePrice from margin
      let computedSalePrice: number | null = null;
      if (priceType === "cost" && margin > 0 && row.price > 0) {
        computedSalePrice = Math.round(row.price * (1 + margin / 100) * 100) / 100;
        updated = { ...updated, salePrice: computedSalePrice };
      }
      // Apply match and pre-fill stock-specific price fields
      if (sel?.mode === "existing" && sel.existingProductId) {
        const stockSalePrice =
          priceType === "sell" && row.price > 0
            ? row.price
            : computedSalePrice != null && computedSalePrice > 0
            ? computedSalePrice
            : null;
        const purchasePrice = priceType === "cost" && row.price > 0 ? row.price : null;
        updated = { ...updated, rowMode: "stock", existingProductId: sel.existingProductId, stockSalePrice, purchasePrice };
      }
      return updated;
    });
    onConfirm(patchedRows);
  };

  const canConfirm = !(priceType === "cost" && !modalSupplier);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={step === "match" ? "md" : "sm"}
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 2,
          }}
        >
          <AutoAwesomeOutlinedIcon color="primary" />
          <Box>
            <Typography fontWeight={800} variant="h6" component="span" display="block">
              {step === "match" ? "Verificar produtos detectados" : "Configurar importação"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {step === "match"
                ? `${newRows.length} produto(s) detectado(s) pela IA`
                : `${newRows.length} produto(s) prontos para importar`}
            </Typography>
          </Box>
        </DialogTitle>

        {step === "match" ? (
          <DialogContent dividers sx={{ p: 0 }}>
            <Stack divider={<Divider />}>
              {rowCandidates.map(({ rowId, row, autoByCode, candidates }) => {
                const sel = matchSelections.find((s) => s.rowId === rowId);
                const radioValue =
                  sel?.mode === "existing" ? (sel.existingProductId ?? "new") : "new";
                const allOptions = [
                  ...(autoByCode ? [autoByCode] : []),
                  ...candidates.filter((c) => c.id !== autoByCode?.id),
                ];
                return (
                  <Box key={rowId} sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      {row.name || "Sem nome"}
                      {row.code ? (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          Código: {row.code}
                        </Typography>
                      ) : null}
                    </Typography>
                    <RadioGroup
                      value={radioValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        setMatchSelections((prev) =>
                          prev.map((s) =>
                            s.rowId === rowId
                              ? { ...s, mode: val === "new" ? "new" : "existing", existingProductId: val === "new" ? null : val }
                              : s
                          )
                        );
                      }}
                    >
                      <FormControlLabel
                        value="new"
                        control={<Radio size="small" />}
                        label={<Typography variant="body2">Cadastrar como novo produto</Typography>}
                      />
                      {allOptions.map((p) => (
                        <FormControlLabel
                          key={p.id}
                          value={p.id}
                          control={<Radio size="small" />}
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                              <Typography variant="body2">{p.getName()}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {p.getCodeLabelForUi()} — Estq: {p.getStock()}
                              </Typography>
                              {p.id === autoByCode?.id ? (
                                <Chip label="código idêntico" size="small" color="success" variant="outlined" />
                              ) : null}
                            </Box>
                          }
                        />
                      ))}
                      {allOptions.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5 }}>
                          Nenhum produto similar encontrado
                        </Typography>
                      ) : null}
                    </RadioGroup>
                  </Box>
                );
              })}
            </Stack>
          </DialogContent>
        ) : (
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Aplicar fornecedor a todos os itens
                </Typography>
                {(() => {
                  type SupOpt = { id: string; title: string; inputValue?: string };
                  const supOptions: SupOpt[] = localSuppliers.map((s) => ({ id: s.id, title: s.title }));
                  const supValue: SupOpt | null = modalSupplier ? { id: modalSupplier.id, title: modalSupplier.title } : null;
                  return (
                    <Autocomplete<SupOpt, false, false, true>
                      size="small"
                      freeSolo
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      options={supOptions}
                      getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.title)}
                      filterOptions={(options, params) => {
                        const filtered = createFilterOptions<SupOpt>()(options, params);
                        const { inputValue } = params;
                        if (inputValue !== "" && !options.some((o) => o.title === inputValue)) {
                          filtered.push({ id: "_new", title: `Adicionar "${inputValue}"`, inputValue });
                        }
                        return filtered;
                      }}
                      value={supValue}
                      onChange={(_, newValue) => {
                        if (!newValue) { setModalSupplier(null); return; }
                        if (typeof newValue === "string") { void createSupplierByTitle(newValue.trim()); return; }
                        if (newValue.id === "_new" && newValue.inputValue) { void createSupplierByTitle(newValue.inputValue.trim()); return; }
                        const found = localSuppliers.find((s) => s.id === newValue.id);
                        if (found) setModalSupplier(found);
                      }}
                      renderOption={(props, option) => <li {...props}>{option.title}</li>}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={priceType === "cost" ? "Selecione (obrigatório)" : "Opcional"}
                          error={priceType === "cost" && !modalSupplier}
                        />
                      )}
                    />
                  );
                })()}
              </Box>

              {hasAnyPrice ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {priceCount === newRows.length
                      ? `Todos os ${newRows.length} produtos têm preço detectado pela IA.`
                      : `${priceCount} de ${newRows.length} produtos têm preço detectado pela IA.`}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    O preço detectado é:
                  </Typography>
                  <RadioGroup
                    row
                    value={priceType}
                    onChange={(e) => setPriceType(e.target.value as "sell" | "cost")}
                  >
                    <FormControlLabel value="sell" control={<Radio size="small" />} label="Preço de venda" />
                    <FormControlLabel value="cost" control={<Radio size="small" />} label="Preço de custo" />
                  </RadioGroup>
                  {priceType === "cost" ? (
                    <Box
                      sx={{
                        mt: 2, p: 2, borderRadius: 2, border: 1,
                        borderColor: "divider",
                        bgcolor: alpha(theme.palette.secondary.main, 0.04),
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        Precificação automática (opcional)
                      </Typography>
                      <TextField
                        size="small"
                        label="Margem de lucro (%)"
                        type="number"
                        value={margin}
                        onChange={(e) => setMargin(Math.max(0, parseFloat(e.target.value) || 0))}
                        inputProps={{ min: 0, step: 1 }}
                        helperText={
                          margin > 0 && firstPricedRow
                            ? `Ex.: custo R$ ${firstPricedRow.price.toFixed(2)} → venda R$ ${exampleSale.toFixed(2)}`
                            : "Deixe 0 para definir o preço de venda depois"
                        }
                        sx={{ maxWidth: 240 }}
                      />
                    </Box>
                  ) : null}
                </Box>
              ) : null}

              {priceType === "cost" && !modalSupplier ? (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Preço de custo exige um fornecedor selecionado.
                </Alert>
              ) : null}
            </Stack>
          </DialogContent>
        )}

        <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          {step === "match" ? (
            <>
              <Button
                onClick={() => setSkipConfirmOpen(true)}
                color="inherit"
                sx={{ textTransform: "none" }}
              >
                Pular ›
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() => proceedToConfig(matchSelections)}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Próximo
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                disabled={!canConfirm}
                onClick={handleConfirm}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                Confirmar e revisar
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={skipConfirmOpen}
        onClose={() => setSkipConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>Pular verificação?</DialogTitle>
        <DialogContent>
          <Typography>
            Os produtos com código idêntico serão associados automaticamente. Os demais serão cadastrados como novos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipConfirmOpen(false)} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSkipConfirmed}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Pular e continuar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function BulkProductsPage() {
  const moduleAccess = useModuleAccess();
  application.useHasPermission(Permission.WRITE_PRODUCTS);
  const theme = useTheme();
  const [user] = application.user.useState();

  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [suppliers, setSuppliers] = useState<ProductSupplier[]>([]);
  const [catalog, setCatalog] = useState<Product[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  const [tab, setTab] = useState<"texto" | "csv" | "media">("texto");
  const [textContent, setTextContent] = useState("");
  const [csvContent, setCsvContent] = useState("");

  const [rows, setRows] = useState<DraftRow[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiResultModal, setAiResultModal] = useState<AiResultModalData | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, s, products] = await Promise.all([
          productCategoriesRepository.getAll(),
          productSupplierRepository.getAll(),
          productRepository.getAll("name"),
        ]);
        if (!cancelled) {
          setCategories(c);
          setSuppliers(s);
          setCatalog(products);
        }
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hints = useMemo(
    () => ({
      categoryNamesHint: categories.map((c) => c.title).join(", "),
      supplierNamesHint: suppliers.map((s) => s.title).join(", "),
    }),
    [categories, suppliers]
  );

  const nameMatchByRow = useMemo(() => {
    const m = new Map<string, Product[]>();
    for (const r of rows) {
      if (r.rowMode !== "create") continue;
      const { autoByCode, candidatesByName } = matchCatalogForBulkRow(
        { code: r.code, name: r.name },
        catalog
      );
      if (autoByCode) continue;
      if (candidatesByName.length > 0) {
        m.set(r.id, candidatesByName);
      }
    }
    return m;
  }, [rows, catalog]);

  const runAi = async (payload: {
    textContent?: string;
    mediaBase64?: string;
    mimeType?: string;
  }) => {
    setError(null);
    setAiLoading(true);
    try {
      const result = await aiBulkProductsExtract({
        ...payload,
        categoryNamesHint: hints.categoryNamesHint,
        supplierNamesHint: hints.supplierNamesHint,
      });
      const next = mapAiToRows(result.products, categories, suppliers);
      if (next.length === 0) {
        setError("A IA não retornou produtos. Tente outro texto ou arquivo.");
        return;
      }
      setAiResultModal({ newRows: next });
    } catch (e) {
      if (isInsufficientAiCreditsError(e)) return;
      setError(e instanceof Error ? e.message : "Falha na extração.");
    } finally {
      setAiLoading(false);
    }
  };

  const onProcessTextAi = () => {
    const t = textContent.trim();
    if (!t) {
      setError("Cole ou digite o conteúdo antes de processar.");
      return;
    }
    runAi({ textContent: t });
  };

  const onProcessCsvAi = () => {
    const t = csvContent.trim();
    if (!t) {
      setError("Cole o CSV ou planilha antes de processar.");
      return;
    }
    runAi({ textContent: t });
  };

  const onImportCsvFast = () => {
    const t = csvContent.trim();
    if (!t) {
      setError("Cole o CSV antes.");
      return;
    }
    try {
      const parsed = parseProductsFromCsv(t);
      if (parsed.length === 0) {
        setError("Nenhuma linha válida encontrada.");
        return;
      }
      const next: DraftRow[] = parsed.map((p) => ({
        id: v4(),
        rowMode: "create" as const,
        existingProductId: null,
        code: p.code ?? "",
        name: p.name,
        price: p.price ?? 0,
        salePrice: null,
        priceType: "sell" as const,
        stockSalePrice: null,
        purchasePrice: null,
        stock: p.stock ?? 1,
        categoryId: findBestCategoryId(p.categoryName, categories),
        supplierId: findBestSupplierId(p.supplierName, suppliers),
        suggestedCategoryId: null,
        suggestedCategoryName: null,
        suggestedSupplierId: null,
        suggestedSupplierName: null,
        trackStock: true,
      }));
      setRows((prev) => [...prev, ...next]);
      setError(null);
    } catch {
      setError("Não foi possível ler o CSV.");
    }
  };

  const onPickMedia = async (file: File | null) => {
    if (!file) return;
    const mime = file.type.split(";")[0].trim().toLowerCase();
    if (file.size > 18 * 1024 * 1024) {
      await Swal.fire({ icon: "error", title: "Arquivo grande", text: "Máximo 18 MB." });
      return;
    }
    setError(null);
    try {
      const base64 = await readFileAsBase64(file);
      await runAi({ mediaBase64: base64, mimeType: mime || "application/octet-stream" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao ler arquivo.");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const rec = new MediaRecorder(
        stream,
        MediaRecorder.isTypeSupported("audio/webm") ? { mimeType: "audio/webm" } : undefined
      );
      mediaRecorderRef.current = rec;
      rec.ondataavailable = (ev) => {
        if (ev.data.size) chunksRef.current.push(ev.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blobType = rec.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: blobType });
        const ext = blobType.includes("webm") ? "webm" : blobType.includes("mp4") ? "m4a" : "dat";
        const file = new File([blob], `nota-voz-${Date.now()}.${ext}`, { type: blobType });
        void onPickMedia(file);
      };
      rec.start();
      setRecording(true);
    } catch {
      await Swal.fire({
        icon: "error",
        title: "Microfone",
        text: "Permita o uso do microfone ou envie um arquivo de áudio.",
      });
    }
  };

  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      rec.stop();
    }
    mediaRecorderRef.current = null;
    setRecording(false);
  };

  const updateRow = useCallback(
    (id: string, patch: Partial<DraftRow>) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          let next: DraftRow = { ...r, ...patch };
          if (next.rowMode === "create" && catalog.length && !("rowMode" in patch)) {
            const { autoByCode } = matchCatalogForBulkRow(
              { code: next.code, name: next.name },
              catalog
            );
            if (autoByCode) {
              next = draftRowToStockMode(next, autoByCode);
            }
          }
          return next;
        })
      );
    },
    [catalog]
  );

  useEffect(() => {
    if (!catalog.length) return;
    setRows((prev) =>
      prev.map((r) => {
        if (r.rowMode !== "create") return r;
        const { autoByCode } = matchCatalogForBulkRow(
          { code: r.code, name: r.name },
          catalog
        );
        if (autoByCode) {
          return draftRowToStockMode(r, autoByCode);
        }
        return r;
      })
    );
  }, [catalog]);

  useEffect(() => {
    if (!categories.length && !suppliers.length) return;
    setRows((prev) =>
      prev.map((r) => {
        if (r.rowMode !== "create") return r;
        let next = r;

        if (!next.categoryId && next.suggestedCategoryName) {
          const matched = findBestCategoryId(next.suggestedCategoryName, categories);
          if (matched && matched !== next.suggestedCategoryId) {
            next = { ...next, suggestedCategoryId: matched };
          }
        }
        if (!next.supplierId && next.suggestedSupplierName) {
          const matched = findBestSupplierId(next.suggestedSupplierName, suppliers);
          if (matched && matched !== next.suggestedSupplierId) {
            next = { ...next, suggestedSupplierId: matched };
          }
        }
        return next;
      })
    );
  }, [categories, suppliers]);

  const applyExistingForStock = (rowId: string, p: Product) => {
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? draftRowToStockMode(r, p) : r))
    );
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const applyCategorySuggestionsExistingOnly = useCallback(() => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.rowMode !== "create") return r;
        if (r.categoryId || !r.suggestedCategoryId) return r;
        return {
          ...r,
          categoryId: r.suggestedCategoryId,
          suggestedCategoryId: null,
          suggestedCategoryName: null,
        };
      })
    );
  }, []);

  const applyAllCategorySuggestions = useCallback(async () => {
    const missingNames = Array.from(
      new Set(
        rows
          .filter(
            (r) =>
              r.rowMode === "create" &&
              !r.categoryId &&
              !r.suggestedCategoryId &&
              Boolean(r.suggestedCategoryName?.trim())
          )
          .map((r) => r.suggestedCategoryName!.trim())
      )
    );

    const createdByNormalized = new Map<string, string>();
    for (const title of missingNames) {
      const existingId = findBestCategoryId(title, categories);
      if (existingId) {
        createdByNormalized.set(normalizeLabel(title), existingId);
        continue;
      }
      const created = new ProductCategoryEntity(v4(), title, "");
      await productCategoriesRepository.save(created);
      createdByNormalized.set(normalizeLabel(title), created.id);
      setCategories((prev) => [...prev, created]);
    }

    setRows((prev) =>
      prev.map((r) => {
        if (r.rowMode !== "create" || r.categoryId) return r;
        if (r.suggestedCategoryId) {
          return {
            ...r,
            categoryId: r.suggestedCategoryId,
            suggestedCategoryId: null,
            suggestedCategoryName: null,
          };
        }
        const name = r.suggestedCategoryName?.trim();
        if (!name) return r;
        const createdId = createdByNormalized.get(normalizeLabel(name));
        if (!createdId) return r;
        return {
          ...r,
          categoryId: createdId,
          suggestedCategoryId: null,
          suggestedCategoryName: null,
        };
      })
    );
  }, [rows, categories]);

  const onApplyCategorySuggestions = useCallback(async () => {
    const hasAnyCategorySuggestion = rows.some(
      (r) =>
        r.rowMode === "create" &&
        !r.categoryId &&
        (Boolean(r.suggestedCategoryId) || Boolean(r.suggestedCategoryName?.trim()))
    );
    if (!hasAnyCategorySuggestion) {
      await Swal.fire({
        icon: "info",
        title: "Sem sugestões",
        text: "Não há sugestões de categoria pendentes para aplicar.",
      });
      return;
    }

    const choice = await Swal.fire({
      title: "Aplicar sugestões de categoria",
      text: "Você quer aplicar só categorias já existentes ou aplicar tudo e criar as que faltam?",
      icon: "question",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Somente existentes",
      denyButtonText: "Aplicar tudo e criar faltantes",
      cancelButtonText: "Cancelar",
    });

    if (choice.isConfirmed) {
      applyCategorySuggestionsExistingOnly();
      return;
    }

    if (choice.isDenied) {
      await applyAllCategorySuggestions();
    }
  }, [rows, applyCategorySuggestionsExistingOnly, applyAllCategorySuggestions]);

  const createCategoryAndAssign = useCallback(
    async (rowId: string, initialTitle?: string | null) => {
      const input = await Swal.fire({
        title: "Nova categoria",
        input: "text",
        inputValue: initialTitle?.trim() || "",
        inputLabel: "Nome da categoria",
        showCancelButton: true,
        confirmButtonText: "Criar e usar",
        cancelButtonText: "Cancelar",
      });
      const title = String(input.value ?? "").trim();
      if (!input.isConfirmed || !title) return;
      const created = new ProductCategoryEntity(v4(), title, "");
      await productCategoriesRepository.save(created);
      setCategories((prev) => [...prev, created]);
      setRows((prev) =>
        prev.map((r) => {
          if (r.rowMode !== "create" || r.categoryId) return r;
          if (!r.suggestedCategoryName) return r;
          return normalizeLabel(r.suggestedCategoryName) === normalizeLabel(title) ?
              { ...r, suggestedCategoryId: created.id } :
              r;
        })
      );
      updateRow(rowId, {
        categoryId: created.id,
        suggestedCategoryId: null,
        suggestedCategoryName: null,
      });
    },
    [updateRow]
  );

  const createSupplierAndAssign = useCallback(
    async (rowId: string, initialTitle?: string | null) => {
      const input = await Swal.fire({
        title: "Novo fornecedor",
        input: "text",
        inputValue: initialTitle?.trim() || "",
        inputLabel: "Nome do fornecedor",
        showCancelButton: true,
        confirmButtonText: "Criar e usar",
        cancelButtonText: "Cancelar",
      });
      const title = String(input.value ?? "").trim();
      if (!input.isConfirmed || !title) return;
      const created = new ProductSupplierEntity(v4(), title);
      await productSupplierRepository.save(created);
      setSuppliers((prev) => [...prev, created]);
      setRows((prev) =>
        prev.map((r) => {
          if (r.rowMode !== "create" || r.supplierId) return r;
          if (!r.suggestedSupplierName) return r;
          return normalizeLabel(r.suggestedSupplierName) === normalizeLabel(title) ?
              { ...r, suggestedSupplierId: created.id } :
              r;
        })
      );
      updateRow(rowId, {
        supplierId: created.id,
        suggestedSupplierId: null,
        suggestedSupplierName: null,
      });
    },
    [updateRow]
  );

  const createSupplierByTitleAndAssign = useCallback(
    async (rowId: string, title: string) => {
      if (!title) return;
      const created = new ProductSupplierEntity(v4(), title);
      await productSupplierRepository.save(created);
      setSuppliers((prev) => [...prev, created]);
      updateRow(rowId, { supplierId: created.id });
    },
    [updateRow]
  );

  const saveAll = async () => {
    const validStock = rows.filter((r) => r.rowMode === "stock" && r.existingProductId);
    const validCreate = rows.filter((r) => r.rowMode === "create" && r.name.trim().length > 0);
    if (validStock.length === 0 && validCreate.length === 0) {
      setError("Inclua linhas válidas (novo produto com nome ou somar estoque com produto selecionado).");
      return;
    }
    for (const r of validCreate) {
      const c = r.code?.trim();
      if (c) {
        const taken = await productRepository.findByCode(c);
        if (taken) {
          setError(
            `O código "${c}" já está em uso por "${taken.getName()}". Ajuste o código, use "Somar estoque" ou remova a linha.`
          );
          return;
        }
      }
    }
    setSaveLoading(true);
    setError(null);
    try {
      let created = 0;
      let stockUpdates = 0;
      for (const r of rows) {
        if (r.rowMode === "stock" && r.existingProductId) {
          const existing = await productRepository.findById(r.existingProductId);
          if (existing.getTrackStock()) {
            existing.setStock(existing.getStock() + Math.max(0, r.stock));
          }
          // Sale price: explicit field takes priority, then priceType fallback (AI-matched rows)
          const newSalePrice =
            r.stockSalePrice != null && r.stockSalePrice > 0
              ? r.stockSalePrice
              : r.priceType === "sell" && r.price > 0
              ? r.price
              : r.priceType === "cost" && r.salePrice != null && r.salePrice > 0
              ? r.salePrice
              : null;
          if (newSalePrice != null) existing.setPrice(newSalePrice);
          const stockSup = r.supplierId
            ? await productSupplierRepository.findById(r.supplierId).catch(() => null)
            : null;
          // Purchase price: explicit field takes priority, then priceType fallback
          const purchasePriceToUse =
            r.purchasePrice != null && r.purchasePrice > 0
              ? r.purchasePrice
              : r.priceType === "cost" && r.price > 0
              ? r.price
              : 0;
          if (stockSup) {
            const currentCosts = existing.getSupplierCosts();
            const alreadyHas = currentCosts.find((c) => c.supplier.id === stockSup.id);
            if (alreadyHas) {
              if (purchasePriceToUse > 0) {
                existing.setSupplierCosts(
                  currentCosts.map((c) =>
                    c.supplier.id === stockSup.id ? { ...c, purchasePrice: purchasePriceToUse } : c
                  )
                );
              }
            } else {
              existing.setSupplierCosts([...currentCosts, { supplier: stockSup, purchasePrice: purchasePriceToUse }]);
            }
          }
          await productRepository.save(existing);
          const salePriceWasChanged = newSalePrice != null;
          const costWasChanged = stockSup != null && purchasePriceToUse > 0;
          if (salePriceWasChanged || costWasChanged) {
            try {
              await recordPriceHistory(existing.id, {
                salePrice: existing.getPrice(),
                supplierCosts: existing.getSupplierCosts().map(c => ({
                  supplierId: c.supplier.id,
                  supplierName: c.supplier.title ?? c.supplier.id,
                  purchasePrice: c.purchasePrice,
                })),
                changedByUserId: user?.id ?? "",
                source: "bulk",
              });
            } catch { /* não fatal */ }
          }
          stockUpdates += 1;
          continue;
        }
        if (r.rowMode !== "create" || r.name.trim().length === 0) continue;
        const cat = r.categoryId
          ? await productCategoriesRepository.findById(r.categoryId).catch(() => null)
          : null;
        const sup = r.supplierId
          ? await productSupplierRepository.findById(r.supplierId).catch(() => null)
          : null;
        const effectiveSalePrice =
          r.priceType === "sell"
            ? Math.max(0, r.price)
            : r.salePrice != null
            ? Math.max(0, r.salePrice)
            : 0;
        const supplierCosts: ProductSupplierCostEntry[] = sup
          ? [{ supplier: sup, purchasePrice: r.priceType === "cost" ? Math.max(0, r.price) : 0 }]
          : [];
        const product = new Product(
          v4(),
          r.name.trim(),
          effectiveSalePrice,
          noImage,
          cat,
          sup,
          Math.max(0, r.stock),
          r.trackStock,
          r.code?.trim() || null,
          "",
          supplierCosts
        );
        await productRepository.save(product);
        try {
          await recordPriceHistory(product.id, {
            salePrice: product.getPrice(),
            supplierCosts: product.getSupplierCosts().map(c => ({
              supplierId: c.supplier.id,
              supplierName: c.supplier.title ?? c.supplier.id,
              purchasePrice: c.purchasePrice,
            })),
            changedByUserId: user?.id ?? "",
            source: "bulk",
          });
        } catch { /* não fatal */ }
        created += 1;
      }
      await Swal.fire({
        icon: "success",
        title: "Concluído",
        text: `${created} novo(s) cadastro(s), ${stockUpdates} atualização(ões) de estoque.`,
      });
      setRows([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao salvar.");
    } finally {
      setSaveLoading(false);
    }
  };

  if (loadingMeta) {
    return (
      <Shell title="Cadastro em massa">
        <LoadingOverlay message="Carregando categorias…" />
      </Shell>
    );
  }

  if (moduleAccess.loading) {
    return (
      <Shell title="Cadastro em massa">
        <LoadingOverlay message="Carregando módulo…" subtle />
      </Shell>
    );
  }

  if (!moduleAccess.isEnabled("bulk_register")) {
    return (
      <Shell title="Cadastro em massa">
        <PageHeader title="Cadastro em massa" description="Importação e cadastro com IA." />
        <ModuleLockedView
          moduleName="Cadastro em massa"
          groupId="bulk_register"
          details="Ative este módulo para importar e cadastrar produtos em lote."
          features={["Cadastro por foto ou PDF", "Importação em lote de centenas de produtos"]}
        />
      </Shell>
    );
  }

  return (
    <Shell title="Cadastro em massa">
      {aiLoading ? <LoadingOverlay subtle message="Processando com IA…" /> : null}
      {saveLoading ? <LoadingOverlay subtle message="Salvando produtos…" /> : null}

      <PageHeader
        title="Cadastro inteligente em massa"
        description="Cole texto ou CSV, envie foto de nota, vídeo da gôndola, PDF ou áudio. A IA monta uma lista editável; revise e salve tudo de uma vez."
      />

      <CashRegisterReportHero
        overline="Catálogo"
        title="Origem dos dados"
        chips={
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Dica: exporte Excel como CSV. Áudio e vídeo podem ser pesados — use arquivos curtos.
          </Typography>
        }
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
      >
        <Tab value="texto" label="Texto livre" />
        <Tab value="csv" label="Planilha CSV" />
        <Tab value="media" label="Foto / PDF / áudio" />
      </Tabs>

      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      {tab === "texto" ? (
        <Card elevation={0} sx={{ borderRadius: 3, border: 1, borderColor: "divider", mb: 2 }}>
          <CardContent>
            <TextField
              multiline
              minRows={8}
              fullWidth
              placeholder={`Ex.: "Comprei 10 pacotes de arroz 5kg por 24,90 cada, estoque 40; feijão 2kg 8 reais 50 unidades..."`}
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeOutlinedIcon />}
                onClick={() => onProcessTextAi()}
                sx={{ fontWeight: 800, borderRadius: 2, textTransform: "none" }}
              >
                Gerar linhas com IA
              </Button>
              <AiCreditCostHint cost={AI_CREDIT_COSTS.bulkProductsExtract} />
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {tab === "csv" ? (
        <Card elevation={0} sx={{ borderRadius: 3, border: 1, borderColor: "divider", mb: 2 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Com cabeçalhos: nome, codigo, preço, estoque, categoria, fornecedor. Sem cabeçalho: 6 colunas{" "}
              <strong>nome; código; preço; estoque; categoria; fornecedor</strong> ou 5 colunas legado (sem código).
            </Typography>
            <TextField
              multiline
              minRows={10}
              fullWidth
              placeholder={`nome;código;preço;estoque\nArroz 5kg;789123;24.9;12`}
              value={csvContent}
              onChange={(e) => setCsvContent(e.target.value)}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={<TableChartOutlinedIcon />}
                onClick={() => onImportCsvFast()}
                sx={{ fontWeight: 700, borderRadius: 2, textTransform: "none" }}
              >
                Importar CSV (rápido, sem IA)
              </Button>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeOutlinedIcon />}
                onClick={() => onProcessCsvAi()}
                sx={{ fontWeight: 800, borderRadius: 2, textTransform: "none" }}
              >
                Interpretar com IA
              </Button>
              <AiCreditCostHint cost={AI_CREDIT_COSTS.bulkProductsExtract} />
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {tab === "media" ? (
        <Card elevation={0} sx={{ borderRadius: 3, border: 1, borderColor: "divider", mb: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Button variant="outlined" component="label" sx={{ alignSelf: "flex-start", borderRadius: 2 }}>
                Escolher arquivo
                <input
                  type="file"
                  hidden
                  accept="image/*,application/pdf,video/*,audio/*"
                  onChange={(e) => onPickMedia(e.target.files?.[0] ?? null)}
                />
              </Button>
              <AiCreditCostHint cost={AI_CREDIT_COSTS.bulkProductsExtract} />
              <Stack direction="row" spacing={1} alignItems="center">
                {!recording ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<MicIcon />}
                    onClick={() => startRecording()}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                  >
                    Gravar nota de voz
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<StopIcon />}
                    onClick={() => stopRecording()}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                  >
                    Parar e enviar
                  </Button>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Após parar a gravação, o áudio é enviado automaticamente para a IA.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }} alignItems="center">
        <Typography variant="h6" fontWeight={800} sx={{ mr: 0.5 }}>
          Revisão ({rows.length} linhas)
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setRows((p) => [...p, emptyRow()])}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Linha vazia
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="secondary"
          disabled={rows.length === 0 || saveLoading}
          onClick={() => void onApplyCategorySuggestions()}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Sugestões
        </Button>
        <Button
          variant="contained"
          size="small"
          color="success"
          disabled={rows.length === 0 || saveLoading}
          onClick={() => saveAll()}
          sx={{ fontWeight: 800, borderRadius: 2, textTransform: "none" }}
        >
          Salvar todos
        </Button>
        <Button size="small" component={Link} to="/produtos" sx={{ textTransform: "none" }}>
          Voltar
        </Button>
      </Stack>

      {rows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
          <Typography>Nenhuma linha ainda. Use as abas acima para gerar ou adicione manualmente.</Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {rows.map((r) => (
            <Paper
              key={r.id}
              elevation={0}
              sx={{ border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden" }}
            >
              {/* Card header */}
              <Box
                sx={{
                  px: 2, py: 1.5,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  borderBottom: 1, borderColor: "divider",
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                }}
              >
                <TextField
                  select size="small" value={r.rowMode}
                  onChange={(e) => {
                    const mode = e.target.value as "create" | "stock";
                    updateRow(r.id, {
                      rowMode: mode,
                      existingProductId: mode === "create" ? null : r.existingProductId,
                    });
                  }}
                  sx={{ width: 170 }}
                >
                  <MenuItem value="create">Novo cadastro</MenuItem>
                  <MenuItem value="stock">Somar estoque</MenuItem>
                </TextField>
                <Tooltip title="Remover">
                  <IconButton size="small" onClick={() => removeRow(r.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Card body */}
              <Box sx={{ p: 2 }}>
                {r.rowMode === "stock" ? (
                  <Grid container spacing={1.5}>
                    {/* Product */}
                    <Grid item xs={12}>
                      <Autocomplete
                        size="small"
                        options={catalog}
                        getOptionLabel={(p) => `${p.getName() || "Sem nome"} — ${p.getCodeLabelForUi()}`}
                        value={catalog.find((x) => x.id === r.existingProductId) ?? null}
                        onChange={(_, p) => {
                          if (!p) {
                            updateRow(r.id, { existingProductId: null, name: "", code: "", price: 0, supplierId: null, stockSalePrice: null, purchasePrice: null });
                            return;
                          }
                          const cat = p.getCategory();
                          const sup = p.getSulier();
                          updateRow(r.id, {
                            existingProductId: p.id, name: p.getName(),
                            code: p.getCode() ?? "", price: p.getPrice(),
                            stockSalePrice: p.getPrice() > 0 ? p.getPrice() : null,
                            categoryId: cat?.id ?? null, supplierId: sup?.id ?? null,
                            suggestedCategoryId: null, suggestedCategoryName: null,
                            suggestedSupplierId: null, suggestedSupplierName: null,
                            trackStock: p.getTrackStock(),
                          });
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Produto cadastrado" placeholder="Buscar produto..." size="small" />
                        )}
                      />
                    </Grid>
                    {/* Qty + Sale price */}
                    <Grid item xs={6}>
                      <TextField
                        size="small" fullWidth label="Quantidade a somar"
                        type="number" inputProps={{ min: 0, step: 1 }}
                        value={r.stock}
                        onChange={(e) => updateRow(r.id, { stock: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CurrencyField
                        fullWidth label="Preço de venda"
                        value={r.stockSalePrice}
                        onChange={(v) => updateRow(r.id, { stockSalePrice: v })}
                        helperText={r.price > 0 ? `Preço atual: R$ ${r.price.toFixed(2)}` : "Opcional"}
                      />
                    </Grid>
                    {/* Supplier freeSolo */}
                    <Grid item xs={r.supplierId ? 7 : 12}>
                      {(() => {
                        type SupOpt = { id: string; title: string; inputValue?: string };
                        const rowSupOptions: SupOpt[] = suppliers.map((s) => ({ id: s.id, title: s.title }));
                        const rowSupValue: SupOpt | null = r.supplierId
                          ? (suppliers.find((s) => s.id === r.supplierId) ? { id: r.supplierId, title: suppliers.find((s) => s.id === r.supplierId)!.title } : null)
                          : null;
                        return (
                          <Autocomplete<SupOpt, false, false, true>
                            size="small" freeSolo selectOnFocus clearOnBlur handleHomeEndKeys
                            options={rowSupOptions}
                            getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.title)}
                            filterOptions={(options, params) => {
                              const filtered = createFilterOptions<SupOpt>()(options, params);
                              const { inputValue } = params;
                              if (inputValue !== "" && !options.some((o) => o.title === inputValue)) {
                                filtered.push({ id: "_new", title: `Adicionar "${inputValue}"`, inputValue });
                              }
                              return filtered;
                            }}
                            value={rowSupValue}
                            onChange={(_, nv) => {
                              if (!nv) { updateRow(r.id, { supplierId: null, purchasePrice: null }); return; }
                              if (typeof nv === "string") { void createSupplierByTitleAndAssign(r.id, nv.trim()); return; }
                              if (nv.id === "_new" && nv.inputValue) { void createSupplierByTitleAndAssign(r.id, nv.inputValue.trim()); return; }
                              const found = suppliers.find((s) => s.id === nv.id);
                              if (found) updateRow(r.id, { supplierId: found.id });
                            }}
                            renderOption={(props, option) => <li {...props}>{option.title}</li>}
                            renderInput={(params) => (
                              <TextField {...params} label="Fornecedor" placeholder="Opcional" />
                            )}
                          />
                        );
                      })()}
                    </Grid>
                    {/* Purchase price — only when supplier selected */}
                    {r.supplierId ? (
                      <Grid item xs={5}>
                        <CurrencyField
                          fullWidth label="Preço de custo"
                          value={r.purchasePrice}
                          onChange={(v) => updateRow(r.id, { purchasePrice: v })}
                        />
                      </Grid>
                    ) : null}
                  </Grid>
                ) : (
                  <Grid container spacing={1.5}>
                    {/* Name */}
                    <Grid item xs={12}>
                      <TextField
                        size="small" fullWidth label="Nome do produto"
                        value={r.name}
                        onChange={(e) => updateRow(r.id, { name: e.target.value })}
                      />
                      {(nameMatchByRow.get(r.id)?.length ?? 0) > 0 ? (
                        <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center" sx={{ mt: 0.75 }}>
                          <Typography variant="caption" color="text.secondary">Já cadastrado?</Typography>
                          {nameMatchByRow.get(r.id)!.slice(0, 4).map((p) => (
                            <Chip
                              key={p.id} size="small"
                              label={`Somar: ${(p.getName() || "Sem nome").slice(0, 22)}${(p.getName() || "").length > 22 ? "…" : ""}`}
                              onClick={() => applyExistingForStock(r.id, p)}
                              variant="outlined" color="secondary"
                            />
                          ))}
                        </Stack>
                      ) : null}
                    </Grid>

                    {/* Code + Qty */}
                    <Grid item xs={6}>
                      <TextField
                        size="small" fullWidth label="Código"
                        value={r.code}
                        onChange={(e) => updateRow(r.id, { code: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        size="small" fullWidth label="Estoque inicial"
                        type="number" inputProps={{ min: 0, step: 1 }}
                        value={r.stock}
                        onChange={(e) => updateRow(r.id, { stock: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                      />
                    </Grid>

                    {/* Price */}
                    <Grid item xs={r.priceType === "cost" ? 6 : 12}>
                      <CurrencyField
                        fullWidth
                        label={r.priceType === "cost" ? "Preço de custo" : "Preço de venda"}
                        value={r.price > 0 ? r.price : null}
                        onChange={(v) => updateRow(r.id, { price: v ?? 0 })}
                      />
                    </Grid>
                    {r.priceType === "cost" ? (
                      <Grid item xs={6}>
                        <CurrencyField
                          fullWidth label="Preço de venda"
                          value={r.salePrice}
                          onChange={(v) => updateRow(r.id, { salePrice: v })}
                          helperText="Calculado pela margem (editável)"
                        />
                      </Grid>
                    ) : null}

                    {/* Category */}
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={0.5} alignItems="flex-start">
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            select size="small" fullWidth label="Categoria"
                            value={r.categoryId ?? ""}
                            onChange={(e) =>
                              updateRow(r.id, {
                                categoryId: e.target.value === "" ? null : e.target.value,
                                suggestedCategoryId: null, suggestedCategoryName: null,
                              })
                            }
                          >
                            <MenuItem value="">—</MenuItem>
                            {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>)}
                          </TextField>
                          {!r.categoryId && (r.suggestedCategoryId || r.suggestedCategoryName) ? (
                            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {r.suggestedCategoryId ? (
                                <Chip size="small" color="secondary" variant="outlined"
                                  label={`Sugestão: ${categories.find((c) => c.id === r.suggestedCategoryId)?.title ?? "Categoria"}`}
                                  onClick={() => updateRow(r.id, { categoryId: r.suggestedCategoryId, suggestedCategoryId: null, suggestedCategoryName: null })}
                                />
                              ) : null}
                              {!r.suggestedCategoryId && r.suggestedCategoryName ? (
                                <Chip size="small" color="secondary" variant="outlined"
                                  label={`Criar: ${r.suggestedCategoryName}`}
                                  onClick={() => void createCategoryAndAssign(r.id, r.suggestedCategoryName)}
                                />
                              ) : null}
                            </Stack>
                          ) : null}
                        </Box>
                        <Tooltip title="Criar categoria">
                          <IconButton size="small" sx={{ mt: 0.5 }} onClick={() => void createCategoryAndAssign(r.id)}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Grid>

                    {/* Supplier */}
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={0.5} alignItems="flex-start">
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            select size="small" fullWidth label="Fornecedor"
                            value={r.supplierId ?? ""}
                            onChange={(e) =>
                              updateRow(r.id, {
                                supplierId: e.target.value === "" ? null : e.target.value,
                                suggestedSupplierId: null, suggestedSupplierName: null,
                              })
                            }
                          >
                            <MenuItem value="">—</MenuItem>
                            {suppliers.map((s) => <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>)}
                          </TextField>
                          {!r.supplierId && (r.suggestedSupplierId || r.suggestedSupplierName) ? (
                            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                              {r.suggestedSupplierId ? (
                                <Chip size="small" color="secondary" variant="outlined"
                                  label={`Sugestão: ${suppliers.find((s) => s.id === r.suggestedSupplierId)?.title ?? "Fornecedor"}`}
                                  onClick={() => updateRow(r.id, { supplierId: r.suggestedSupplierId, suggestedSupplierId: null, suggestedSupplierName: null })}
                                />
                              ) : null}
                              {!r.suggestedSupplierId && r.suggestedSupplierName ? (
                                <Chip size="small" color="secondary" variant="outlined"
                                  label={`Criar: ${r.suggestedSupplierName}`}
                                  onClick={() => void createSupplierAndAssign(r.id, r.suggestedSupplierName)}
                                />
                              ) : null}
                            </Stack>
                          ) : null}
                        </Box>
                        <Tooltip title="Criar fornecedor">
                          <IconButton size="small" sx={{ mt: 0.5 }} onClick={() => void createSupplierAndAssign(r.id)}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Grid>

                    {/* Track stock */}
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={r.trackStock}
                            onChange={(e) => updateRow(r.id, { trackStock: e.target.checked })}
                          />
                        }
                        label="Rastrear estoque"
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      {aiResultModal ? (
        <AiResultModalDialog
          open
          newRows={aiResultModal.newRows}
          catalog={catalog}
          suppliers={suppliers}
          onConfirm={(patchedRows) => {
            setRows((prev) => [...prev, ...patchedRows]);
            setAiResultModal(null);
          }}
          onClose={() => setAiResultModal(null)}
          onSupplierCreated={(s) => setSuppliers((prev) => [...prev, s])}
        />
      ) : null}
    </Shell>
  );
}
